// The account row (sign-in; protocol §3.2): the bottom of the money card, the home screen's door
// to accounts. Free play never needs it; it says so.
// - signed out: "Sign in with email, Google, X or a Solana wallet to play for money." and a blue
//   "Sign in" button
//   (the first press loads the sign-in module: Privy's code is fetched only then);
// - busy (loading the module, restoring a session, the sign-in modal open): "Signing in…" with a
//   small spinner, nothing clickable;
// - signed in: "Signed in as <username>" (or the email and a "Pick a username" link while the
//   username is not set), the payment wallet's short address (click copies the whole address;
//   "Setting up your wallet…" until the server knows it) with a small muted tag for its kind
//   ("built-in" for the Privy-made wallet, "external" for Phantom & co.; none when unknown), a
//   wallet problem line instead of "Setting up…" when the server has no usable payment wallet
//   (unlinked in Privy: the address stays, without the kind tag; taken by another account;
//   setup failed), a suspension line when banned, and
//   "Sign out" (disabled during a money life: cash out or die first).
// - hidden while the server offers no sign-in (`/api/info.auth` absent).
//
// `accountRowView` is DOM-free (unit-tested). Consumer: ui/menu/money.ts.

import type { AccountState } from '../../auth/session.js';
import type { UiClickSource } from '../../events/bus.js';
import { h, type ReadonlySignal, show, type Unsubscribe } from '../dom.js';
import { t } from '../i18n/t.js';

/** "7xKX…a9Fz": the first and last 4 characters of an address (whole when ≤ 10). */
export function shortAddress(address: string): string {
  return address.length <= 10 ? address : `${address.slice(0, 4)}…${address.slice(-4)}`;
}

/** The suspension line, or null when `bannedUntil` is not in the future. */
export function banLine(bannedUntil: number | null | undefined, nowMs: number): string | null {
  if (bannedUntil === null || bannedUntil === undefined || !(bannedUntil > nowMs)) return null;
  if (!Number.isFinite(bannedUntil) || bannedUntil > nowMs + 50 * 365 * 86_400_000) {
    return t('account.bannedForever');
  }
  let date: string;
  try {
    date = new Date(bannedUntil).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    date = new Date(bannedUntil).toISOString();
  }
  return t('account.bannedUntil', { date });
}

/** True when the account may not play for money now (banned). */
export function isBanned(state: AccountState, nowMs: number): boolean {
  return banLine(state.user?.bannedUntil, nowMs) !== null;
}

export type AccountRowMode = 'hidden' | 'signedOut' | 'busy' | 'signedIn';

export interface AccountRowView {
  readonly mode: AccountRowMode;
  /** The first line ("Sign in with…", "Signing in…", "Signed in as …"). */
  readonly line: string;
  /** Signed in without a username: show "Pick a username". */
  readonly needsUsername: boolean;
  /** Short wallet address, or null (none yet / signed out). */
  readonly wallet: string | null;
  /** The whole address (copied on click). */
  readonly walletFull: string | null;
  /** The wallet's kind tag ("built-in" / "external") and its tooltip, null when unknown. */
  readonly walletKind: { readonly tag: string; readonly title: string } | null;
  /** Signed in, no wallet known yet (and no problem to report). */
  readonly walletPending: boolean;
  /** Why money has no usable payment wallet (AuthUser.walletProblem), null when none. */
  readonly walletProblem: string | null;
  readonly banned: string | null;
  readonly canSignOut: boolean;
  /** The sign-in module could not load. */
  readonly error: string | null;
}

export interface AccountRowInput {
  readonly state: AccountState;
  /** A money life is on screen (sign-out waits for it). */
  readonly moneyLifeActive: boolean;
  readonly nowMs: number;
}

/** What the account row shows (DOM-free). */
export function accountRowView(m: AccountRowInput): AccountRowView {
  const s = m.state;
  const base = {
    needsUsername: false,
    wallet: null,
    walletFull: null,
    walletKind: null,
    walletPending: false,
    walletProblem: null,
    banned: null,
    canSignOut: false,
    error: null,
  };
  switch (s.phase) {
    case 'unavailable':
      return { ...base, mode: 'hidden', line: '' };
    case 'busy':
      return { ...base, mode: 'busy', line: t('account.signingIn') };
    case 'signedOut':
      return {
        ...base,
        mode: 'signedOut',
        line: t('account.signInHint'),
        error: s.loadFailed ? t('account.unavailable') : null,
      };
    case 'signedIn': {
      const u = s.user;
      const name = u?.username ?? u?.email ?? null;
      const wallet = u?.wallet ?? null;
      const problem = u?.walletProblem ?? null;
      // The kind tag says "money games use it": not for a wallet money no longer uses.
      const kind = wallet !== null && problem === null ? (u?.walletKind ?? null) : null;
      return {
        ...base,
        mode: 'signedIn',
        line: name !== null ? t('account.signedInAs', { name }) : t('account.signedIn'),
        needsUsername: u !== null && u.username === null,
        wallet: wallet !== null ? shortAddress(wallet) : null,
        walletFull: wallet,
        walletKind:
          kind === null
            ? null
            : { tag: t(`account.walletKind.${kind}`), title: t(`account.walletKindTitle.${kind}`) },
        walletPending: wallet === null && problem === null,
        walletProblem: problem === null ? null : t(`account.walletProblem.${problem}`),
        banned: banLine(u?.bannedUntil, m.nowMs),
        canSignOut: !m.moneyLifeActive,
      };
    }
  }
}

export interface AccountRowDeps {
  readonly state: ReadonlySignal<AccountState>;
  signIn(): void;
  signOut(): void;
  pickUsername(): void;
  /** Copies the whole wallet address (toast feedback is the app's). */
  copyWallet(address: string): void;
  moneyLifeActive(): boolean;
  playClick?(source: UiClickSource): void;
}

export interface AccountRow {
  readonly el: HTMLElement;
  refresh(): void;
  dispose(): void;
}

export function createAccountRow(deps: AccountRowDeps): AccountRow {
  const line = h('p', { class: 'account-line' });
  const spinner = h('span', { class: 'ring-spinner ring-spinner-sm', 'aria-hidden': 'true' });
  const error = h('p', { class: 'account-error', role: 'alert', hidden: true });
  const signInBtn = h(
    'button',
    {
      type: 'button',
      class: 'btn-small btn-account',
      onClick: () => {
        deps.playClick?.('account');
        deps.signIn();
      },
    },
    t('account.signIn'),
  );
  const nameBtn = h(
    'button',
    {
      type: 'button',
      class: 'account-link',
      onClick: () => {
        deps.playClick?.('account');
        deps.pickUsername();
      },
    },
    t('account.pickUsername'),
  );
  let walletFull: string | null = null;
  const walletAddr = h('span', { class: 'account-wallet-addr' });
  const walletKind = h('span', { class: 'account-wallet-kind', hidden: true });
  const walletBtn = h(
    'button',
    {
      type: 'button',
      class: 'account-wallet',
      title: t('account.copyWallet'),
      'aria-label': t('account.copyWallet'),
      onClick: () => {
        if (walletFull === null) return;
        deps.playClick?.('account');
        deps.copyWallet(walletFull);
      },
    },
    h('span', { class: 'account-wallet-label' }, t('account.walletLabel')),
    walletAddr,
    walletKind,
  );
  const walletPending = h('p', { class: 'account-sub', hidden: true }, t('account.walletPending'));
  const walletProblem = h('p', { class: 'account-error', hidden: true });
  const banned = h('p', { class: 'account-error', hidden: true });
  const signOutBtn = h(
    'button',
    {
      type: 'button',
      class: 'btn-small btn-quiet',
      onClick: () => {
        if (signOutBtn.getAttribute('aria-disabled') === 'true') return;
        deps.playClick?.('account');
        deps.signOut();
      },
    },
    t('account.signOut'),
  );
  const actions = h('div', { class: 'account-actions' }, walletBtn, signOutBtn);
  const el = h(
    'div',
    { class: 'account-row', hidden: true },
    line,
    nameBtn,
    walletPending,
    walletProblem,
    banned,
    error,
    spinner,
    signInBtn,
    actions,
  );

  function render(): void {
    const v = accountRowView({
      state: deps.state.get(),
      moneyLifeActive: deps.moneyLifeActive(),
      nowMs: Date.now(),
    });
    show(el, v.mode !== 'hidden');
    el.dataset['mode'] = v.mode;
    line.textContent = v.line;
    show(spinner, v.mode === 'busy');
    show(signInBtn, v.mode === 'signedOut');
    error.textContent = v.error ?? '';
    show(error, v.error !== null);
    show(nameBtn, v.needsUsername);
    show(actions, v.mode === 'signedIn');
    walletFull = v.walletFull;
    walletAddr.textContent = v.wallet ?? '';
    walletKind.textContent = v.walletKind?.tag ?? '';
    walletKind.title = v.walletKind?.title ?? '';
    show(walletKind, v.walletKind !== null);
    show(walletBtn, v.wallet !== null);
    show(walletPending, v.mode === 'signedIn' && v.walletPending);
    walletProblem.textContent = v.walletProblem ?? '';
    show(walletProblem, v.mode === 'signedIn' && v.walletProblem !== null);
    banned.textContent = v.banned ?? '';
    show(banned, v.banned !== null);
    signOutBtn.setAttribute('aria-disabled', v.canSignOut ? 'false' : 'true');
  }

  const off: Unsubscribe = deps.state.subscribe(() => render());
  render();
  return {
    el,
    refresh: render,
    dispose: off,
  };
}
